(ns iris.auth.api
  "Lightweight authentication state abstraction.
   
   Provides a facade over Firebase auth to reduce direct dependencies.
   All application code should use this API instead of iris.firebase for auth.
   
   WebSocket lifecycle is intentionally managed here: the WS connection is a
   consequence of auth state. When Firebase is eventually replaced by WebSocket,
   only this namespace changes — all call sites remain the same."
  (:require
   [iris.firebase :as fb]
   [iris.globals :as globals]
   [iris.websocket.client :as ws-client]))

;; ============================================================================
;; Authentication State Checks
;; ============================================================================

(defn authenticated?
  "Check if user is authenticated (either via Firebase or JWT).
   
   This is a convenience wrapper that checks Firebase auth state.
   You could also check (some? @globals/fbjwt-payload) instead."
  []
  (fb/authenticated?))

(defn current-user
  "Get current user object from Firebase.
   
   Returns the Firebase user object or nil."
  []
  (fb/current-user))

(defn user-atom
  "Get the Firebase user atom for reactive updates.
   
   Use with @(auth/user-atom) to get current value."
  []
  fb/user-atom)

(defn user-id
  "Get current user ID from JWT payload.
   
   This is independent of Firebase - gets UID from Sokrates JWT."
  []
  (:uid @globals/fbjwt-payload))

;; ============================================================================
;; Authentication Promises
;; ============================================================================

(defn wait-for-user-promise
  "Wait for Firebase user to be authenticated.
   
   Returns a promise that resolves with the Firebase user object."
  []
  (fb/wait-for-auth-user-promise))

(defn wait-for-user
  "Wait for Firebase user to be authenticated (channel-based).
   
   Returns a core.async channel that yields the Firebase user object."
  []
  (fb/wait-for-auth-user))

(defn wait-for-user-with-timeout
  "Wait for Firebase user with timeout.
   
   timeout-ms - Timeout in milliseconds
   callback - Function called with user (or nil if timeout)"
  [timeout-ms callback]
  (fb/wait-for-auth-user-with-timeout timeout-ms callback))

;; ============================================================================
;; Authentication Initialization
;; ============================================================================

(defn on-fbjwt-available!
  "Initialize Firebase authentication with a token.
   Called from :fbjwt-observer in core.cljs when the Firebase JWT arrives.
   
   TEMPORARY — this function and its observer go away when Firebase is removed.
   At that point, on-jwt-available! becomes the sole auth entry point.
   
   token - Firebase custom token (from Sokrates backend)"
  [token]
  (fb/init-auth! token))

;; ============================================================================
;; Authentication Teardown
;; ============================================================================

(defn sign-out!
  "Sign out of all auth systems.
   
   Transition period: clears both Firebase and WebSocket.
   When Firebase is removed: delete the fb/sign-out! line. Nothing else changes.
   
   Called by :jwt-observer when globals/jwt is set to nil."
  []
  (fb/sign-out!)
  (ws-client/disconnect!))

;; ============================================================================
;; JWT Startup — called by :jwt-observer in core.cljs
;; ============================================================================

(defn on-jwt-available!
  "Called when the Iris JWT first becomes available (user logged in).
   
   WebSocket startup is intentionally disabled while the server implementation
   is delayed. When Firebase is removed, this remains the auth lifecycle hook
   for starting replacement API/polling behavior."
  []
  nil)

;; ============================================================================
;; Authentication Callbacks
;; ============================================================================

(defn on-auth-ready
  "Register a callback to be called when authentication is ready.
   
   callback - Function to call with user object when auth completes"
  [callback]
  (fb/on-auth-ready callback))
